import React, { useEffect, useRef, useState } from 'react';
import { Input, Button, Space, Divider, Row, Col, Modal, Tag, Spin, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import AddSvg from '../../assets/images/AddSvg';
import TrashSvg from '../../assets/images/TrashSvg';
import EditSvg from '../../assets/images/EditSvg';
import CategorySvg from '../../assets/images/CategorySvg';
import { NEWS_CATEGORY_URL } from '../../api/URLs';
import { deleteDataRequest, getDataRequest, patchDataRequest, postDataRequest, putDataRequest } from '../../api/serviceMethods';
import MessageConfirmModal from '../../components/messageModal/MessageConfirmModal';
import { MdDragIndicator } from 'react-icons/md';

const NewHeader = ({ getDataListNews }) => {
    const messageModalRef = useRef('');
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const { t } = useTranslation();
    const [newTag, setNewTag] = useState("");
    const [tags, setTags] = useState([]);
    const [tableParams, setTableParams] = useState({
        pagination: {
            current: 1,
            pageSize: 50,
        },
    });

    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [isOrderChanged, setIsOrderChanged] = useState(false);
    const [originalTags, setOriginalTags] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const draggedIndexRef = useRef(null);
    const [editingTagId, setEditingTagId] = useState(null);

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        draggedIndexRef.current = index;
        e.dataTransfer.effectAllowed = "move";
        setTimeout(() => {
            setIsDragging(true);
        }, 0);
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
        draggedIndexRef.current = null;
        setIsDragging(false);
    };

    const handleParentDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";

        const sourceIndex = draggedIndexRef.current;
        if (sourceIndex === null) return;

        const container = e.currentTarget;
        const tagElements = Array.from(container.querySelectorAll('.ant-tag'));
        if (tagElements.length === 0) return;

        let closestTagIndex = -1;
        let minDistance = Infinity;
        let insertAfter = false;

        const mouseX = e.clientX;
        const mouseY = e.clientY;

        tagElements.forEach((el, index) => {
            const rect = el.getBoundingClientRect();
            const tagCenterX = rect.left + rect.width / 2;
            const tagCenterY = rect.top + rect.height / 2;

            const distance = Math.sqrt(
                Math.pow(mouseX - tagCenterX, 2) + Math.pow(mouseY - tagCenterY, 2)
            );

            if (distance < minDistance) {
                minDistance = distance;
                closestTagIndex = index;
                insertAfter = mouseX > tagCenterX;
            }
        });

        if (closestTagIndex === -1) return;

        let targetIndex = closestTagIndex;
        if (insertAfter) {
            targetIndex = closestTagIndex + 1;
        }

        // If hovering over the position it already occupies, do nothing
        if (sourceIndex === targetIndex || sourceIndex === targetIndex - 1) {
            return;
        }

        let newTargetIndex = targetIndex;
        if (sourceIndex < targetIndex) {
            newTargetIndex--;
        }

        // Reorder dynamically in state
        const reorderedTags = [...tags];
        const [draggedItem] = reorderedTags.splice(sourceIndex, 1);
        reorderedTags.splice(newTargetIndex, 0, draggedItem);

        setTags(reorderedTags);
        draggedIndexRef.current = newTargetIndex;
        setDraggedIndex(newTargetIndex);
        setIsOrderChanged(true);
    };

    const handleParentDrop = (e) => {
        e.preventDefault();
        setDraggedIndex(null);
        setDragOverIndex(null);
        draggedIndexRef.current = null;
        setIsDragging(false);
    };

    const handleCancelSort = () => {
        setTags(originalTags);
        setIsOrderChanged(false);
    };

    const handleCloseModalOnly = () => {
        setModalOpen(false);
        setTags([]);
        setIsOrderChanged(false);
        setEditingTagId(null);
        setNewTag("");
    };

    const handleConfirmClose = () => {
        messageModalRef.current.showWarningConfirmClose(
            t("You have unsaved changes. Do you want to discard them and close?"),
            () => {
                handleCloseModalOnly();
            },
            "",
            () => {}
        );
    };

    const handleStartEdit = (tag) => {
        setEditingTagId(tag._id);
        setNewTag(tag.name || "");
    };

    const handleCancelEdit = () => {
        setEditingTagId(null);
        setNewTag("");
    };

    const handleSaveEdit = async (tag) => {
        const trimmedName = newTag.trim();
        if (!trimmedName) {
            setEditingTagId(null);
            setNewTag("");
            return;
        }
        if (trimmedName === tag.name) {
            setEditingTagId(null);
            setNewTag("");
            return;
        }
        // Check duplicate name
        const duplicate = tags.some(t => t._id !== tag._id && t.name === trimmedName);
        if (duplicate) {
            messageModalRef.current.showWarningConfirmsAutoClose(t('The category has exited!'), () => { }, "", true);
            return;
        }

        setLoading(true);
        try {
            const res = await patchDataRequest(`${NEWS_CATEGORY_URL}`, { categoryId: tag._id, name: trimmedName });
            if (res.status === 200) {
                messageModalRef.current.showSuccessConfirmsAutoClose(t(res.message) || t("Updated Successfully"), () => { }, "", true);
                setTags(prev => prev.map(t => t._id === tag._id ? { ...t, name: trimmedName } : t));
                setOriginalTags(prev => prev.map(t => t._id === tag._id ? { ...t, name: trimmedName } : t));
                setNewTag("");
                if (typeof getDataListNews === 'function') {
                    getDataListNews();
                }
            } else {
                messageModalRef.current.showWarningConfirmsAutoClose(t(res.message), () => { }, "", true);
            }
        } catch (err) {
            messageModalRef.current.showWarningConfirmsAutoClose(t("Server Error!"), () => { }, "", true);
        } finally {
            setLoading(false);
            setEditingTagId(null);
        }
    };

    const handleUpdateSortOrder = async (updatedTags) => {
        setLoading(true);
        const items = updatedTags.map((tag, index) => ({
            _id: tag._id,
            sortOrder: index + 1
        }));
        try {
            await putDataRequest(`${NEWS_CATEGORY_URL}`, { items })
                .then((res) => {
                    if (res.status === 200) {
                        messageModalRef.current.showSuccessConfirmsAutoClose(t(res.message) || t("Sorted Successfully"), () => { }, "", true);
                        setIsOrderChanged(false);
                        getDataRequestAll('');
                    } else {
                        messageModalRef.current.showWarningConfirmsAutoClose(t(res.message), () => { }, "", true);
                    }
                    setLoading(false);
                })
                .catch((err) => {
                    setLoading(false);
                    messageModalRef.current.showWarningConfirmsAutoClose(err && err.response && err.response.data && err.response.data.message, () => { }, "", true);
                });
        } catch (error) {
            setLoading(false);
            messageModalRef.current.showWarningConfirmsAutoClose(t(`Server Error!`), () => { }, "", true);
        }
    };
    
    const getDataRequestAll = async (value) => {
        setLoading(true);
        await getDataRequest(`${NEWS_CATEGORY_URL}`, {
            // pageNo: tableParams.pagination.current,
            // pageSize: tableParams.pagination.pageSize,
        }).then((res) => {
            if (res.status === 200) {
                let datas = res?.data ? res?.data.map((it, i) => ({
                    ...it,
                    name: it.name || "",
                })) : [];
                setTags(datas);
                setOriginalTags(datas);
                setIsOrderChanged(false);
            }
            // setTableParams((prev) => ({
            //     ...prev,
            //     pagination: {
            //         ...prev.pagination,
            //         total: res?.total || 0,
            //     },
            // }));
            setLoading(false);
        }).catch((err) => {
            setLoading(false);
            console.log("Error", err);
        });
    };

    useEffect(() => {
        if (modalOpen) {
            getDataRequestAll();
            setIsOrderChanged(false);
            setEditingTagId(null);
            setNewTag("");
        }
    }, [modalOpen]);

    const handleCreate = async (name) => {
        try {
            setLoading(true);
            await postDataRequest(`${NEWS_CATEGORY_URL}`, { name: name })
                .then((res) => {
                    if (res.status === 200) {
                        setNewTag("");
                        messageModalRef.current.showSuccessConfirmsAutoClose(res && t(res.message), () => { }, "", true);
                        getDataRequestAll('');
                    } else {
                        messageModalRef.current.showWarningConfirmsAutoClose(res && t(res.message), () => { }, "", true);
                    }
                    setLoading(false);
                })
                .catch((err) => {
                    setLoading(false);
                    messageModalRef.current.showWarningConfirmsAutoClose(err && err.response && err.response.data && err.response.data.message, () => { }, "", true);
                });
        } catch (error) {
            setLoading(false);
            messageModalRef.current.showWarningConfirmsAutoClose(t(`Server Error!`), () => { }, "", true);
        }
    };
    
    const handleAddTag = async () => {
        if (newTag.trim() !== "") {
            if (editingTagId) {
                const tagToEdit = tags.find(t => t._id === editingTagId);
                if (tagToEdit) {
                    await handleSaveEdit(tagToEdit);
                }
                return;
            }
            let filterData = tags.filter(it => it.name === newTag.trim());
            if (filterData.length > 0) {
                messageModalRef.current.showWarningConfirmsAutoClose(t('The category has exited!'), () => { }, "", true);
                return false;
            }
            if (isOrderChanged) {
                setLoading(true);
                const items = tags.map((tag, index) => ({
                    _id: tag._id,
                    sortOrder: index + 1
                }));
                try {
                    await putDataRequest(`${NEWS_CATEGORY_URL}`, { items })
                        .then(async (res) => {
                            if (res.status === 200) {
                                setIsOrderChanged(false);
                                await handleCreate(newTag.trim());
                            } else {
                                setLoading(false);
                                messageModalRef.current.showWarningConfirmsAutoClose(res.message, () => { }, "", true);
                            }
                        })
                        .catch((err) => {
                            setLoading(false);
                            messageModalRef.current.showWarningConfirmsAutoClose(err && err.response && err.response.data && err.response.data.message, () => { }, "", true);
                        });
                } catch (error) {
                    setLoading(false);
                    messageModalRef.current.showWarningConfirmsAutoClose(`Server Error!`, () => { }, "", true);
                }
            } else {
                handleCreate(newTag.trim());
            }
        }
    };

    const handleDeleteTag = (tag, i) => {
        if (tag._id) {
            messageModalRef.current.showWarningConfirm(t('Are you sure you want to delete this category?'), async () => {
                setLoading(true);
                if (isOrderChanged) {
                    const remainingTags = tags.filter(t => t._id !== tag._id);
                    const items = remainingTags.map((t, index) => ({
                        _id: t._id,
                        sortOrder: index + 1
                    }));
                    try {
                        const res = await putDataRequest(`${NEWS_CATEGORY_URL}`, { items });
                        if (res.status === 200) {
                            setIsOrderChanged(false);
                        }
                    } catch (err) {
                        console.error("Failed to save sort order before deletion", err);
                    }
                }
                await deleteDataRequest(`${NEWS_CATEGORY_URL} `, { name: tag.name }).then((res) => {
                    if (res.status === 200) {
                        messageModalRef.current.showSuccessConfirmsAutoClose(res && t(res.message), () => { }, "", true);
                        if (tag._id === editingTagId) {
                            setEditingTagId(null);
                            setNewTag("");
                        }
                        getDataRequestAll('');
                        setLoading(false);
                    } else {
                        messageModalRef.current.showWarningConfirmsAutoClose(res && t(res.message), () => { }, "", true);
                        setLoading(false);
                    }
                }).catch((err) => {
                    messageModalRef.current.showWarningConfirmsAutoClose(`Server Error!`, () => { }, "", true);
                    setLoading(false);
                });
            }, "", true);
        } else {
            setTags(tags.filter((t) => t !== tag));
        }
    };

    return (
        <>
            <Button
                className=''
                type="text"
                onClick={() => setModalOpen(true)}
                style={{
                    fontSize: '16px',
                    marginRight: "1rem",
                    color: "#e72158",
                    border: "1px solid #e72158"
                }}
            >
                {t('Category Management')}
            </Button>
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <CategorySvg />
                        <span style={{ marginLeft: "10px" }}>{t('Category Management')}</span>
                    </div>
                }
                centered
                width={700}
                open={modalOpen}
                className='custom-modal'
                onOk={() => setModalOpen(false)}
                onCancel={() => {
                    if (isOrderChanged) {
                        handleConfirmClose();
                    } else {
                        handleCloseModalOnly();
                    }
                }}
                cancelButtonProps={{ style: { display: 'none' } }}
                okButtonProps={{ style: { display: 'none' } }}
            >
                <Spin spinning={loading}>
                    <hr />
                    <div style={{ padding: "20px 60px", marginBottom: "10px" }}>
                        <Row className='d-flex align-items-center' gutter={[8, 8]}>
                            <Col md={6}>
                                {t("New Category")} :
                            </Col>
                            <Col md={14}>
                                <Input
                                    size='large'
                                    style={{ marginRight: "8px" }}
                                    placeholder=""
                                    value={newTag}
                                    onChange={(e) => setNewTag(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            handleAddTag();
                                        }
                                    }}
                                />
                            </Col>
                            <Col md={4} className='d-flex align-items-center' style={{ gap: "8px" }}>
                                {editingTagId ? (
                                    <>
                                        <Button
                                            style={{
                                                backgroundColor: "#E50A47",
                                                border: "none",
                                                color: "white",
                                                borderRadius: "6px",
                                                fontWeight: 500,
                                                height: "40px",
                                                padding: "0 19px",
                                            }}
                                            onClick={handleAddTag}
                                        >
                                            {t("Save")}
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        style={{
                                            backgroundColor: "#E50A47",
                                            border: "none",
                                            color: "white",
                                            borderRadius: "6px",
                                            fontWeight: 500,
                                            height: "40px",
                                            padding: "0 20px",
                                        }}
                                        onClick={handleAddTag}
                                    >
                                        {t("Add")}
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </div>
                    <div style={{ borderBottom: "1px dashed #ddd", marginBottom: "16px" }}></div>
                    <div style={{ padding: "10px 60px", marginBottom: "16px" }}>
                        <Row align="top" style={{ width: "100%", paddingBottom: "5px" }}>
                            <Col md={6} style={{ display: "flex", flexDirection: "column", gap: "8px", paddingTop: "4px" }}></Col>
                            <Col md={18}>
                                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "4px", paddingTop: "4px", color: '#545454', fontSize: "12px", fontWeight: 500 }}>
                                    {t("Drag items to reorder categories")}
                                </div>
                            </Col>
                        </Row>
                        <Row align="top" style={{ width: "100%" }}>
                            <Col md={6} style={{ display: "flex", flexDirection: "column", gap: "8px", paddingTop: "4px" }}>
                                <span style={{ 
                                    cursor: "pointer", 
                                    fontSize: "14px",
                                    color: "#333",
                                    alignSelf: "flex-start",
                                    alignItems: 'center'
                                }}>
                                    {t("Category")}:
                                </span>
                            </Col>
                            <Col md={18}>
                                <div 
                                    onDragOver={handleParentDragOver}
                                    onDrop={handleParentDrop}
                                    style={{ 
                                        display: "flex", 
                                        flexWrap: "wrap", 
                                        gap: "8px", 
                                        paddingTop: "4px",
                                    }}
                                >
                                    {tags.map((tag, index) => (
                                        <Tag
                                            key={tag._id || tag.name || index}
                                            closable
                                            onClose={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleDeleteTag(tag);
                                            }}
                                            closeIcon={
                                                <div
                                                    className="d-flex align-items-center"
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            height: "18px",
                                                            width: "1.5px",
                                                            backgroundColor: "white",
                                                            marginLeft: "5px",
                                                            marginRight: "5px",
                                                        }}
                                                    />

                                                    {/* EDIT */}
                                                    <span
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleStartEdit(tag);
                                                        }}
                                                        style={{
                                                            cursor: "pointer",
                                                            display: "flex",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <EditSvg
                                                            width="12"
                                                            height="12"
                                                            fill={dragOverIndex === index ? "white" : "#E50A47"}
                                                        />
                                                    </span>

                                                    <div
                                                        style={{
                                                            height: "18px",
                                                            width: "1.5px",
                                                            backgroundColor: "white",
                                                            marginLeft: "5px",
                                                            marginRight: "5px",
                                                        }}
                                                    />

                                                    {/* DELETE */}
                                                    <span
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                        }}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handleDeleteTag(tag);
                                                        }}
                                                        style={{
                                                            cursor: "pointer",
                                                            display: "flex",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <TrashSvg opacity={1} />
                                                    </span>
                                                </div>
                                            }
                                            draggable={editingTagId !== tag._id}
                                            onDragStart={(e) => handleDragStart(e, index)}
                                            onDragEnd={handleDragEnd}
                                            style={{
                                                backgroundColor:
                                                    dragOverIndex === index
                                                        ? "#E50A47"
                                                        : editingTagId === tag._id
                                                            ? "#F8E2E8"
                                                            : "#fdecf1",
                                                color:
                                                    dragOverIndex === index ? "white" : "#E50A47",
                                                width: "auto",
                                                height: "25px",
                                                border:
                                                    dragOverIndex === index
                                                        ? "1px dashed #E50A47"
                                                        : editingTagId === tag._id
                                                            ? "1px solid #E50A47"
                                                            : "1px solid #fdecf1",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                borderRadius: "6px",
                                                fontSize: "12px",
                                                fontWeight: 500,
                                                cursor:
                                                    editingTagId === tag._id ? "default" : "grab",
                                                opacity: draggedIndex === index ? 0.5 : 1,
                                                pointerEvents: "auto",
                                            }}
                                        >
                                            <MdDragIndicator style={{ marginRight: "4px" }} />
                                            {tag.name || ""}
                                        </Tag>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    </div>
                    {isOrderChanged && (
                        <div className='d-flex justify-content-center mt-3' style={{ gap: "16px" }}>
                            <Button
                                style={{
                                    backgroundColor: "#E50A47",
                                    border: "none",
                                    color: "White",
                                    height: "45px",
                                    width: "180px",
                                    borderRadius: "10px",
                                    fontSize: "16px",
                                    fontWeight: 500
                                }}
                                onClick={() => handleUpdateSortOrder(tags)}
                            >
                                {t("Save Order")}
                            </Button>
                            <Button
                                style={{
                                    backgroundColor: "#f5f5f5",
                                    border: "1px solid #d9d9d9",
                                    color: "rgba(0, 0, 0, 0.85)",
                                    height: "45px",
                                    width: "180px",
                                    borderRadius: "10px",
                                    fontSize: "16px",
                                    fontWeight: 500
                                }}
                                onClick={handleCancelSort}
                            >
                                {t("Cancel")}
                            </Button>
                        </div>
                    )}
                </Spin>
            </Modal>
            <MessageConfirmModal textCentered ref={messageModalRef} />
        </>
    );
};

export default NewHeader;